import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { authClient } from "../../lib/auth-client";
import { Button, Card, Field, Input, PageTitle } from "../../components/ui";

export function LoginPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needTotp, setNeedTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function finish() {
    await qc.invalidateQueries({ queryKey: ["me"] });
    navigate("/app");
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await authClient.signIn.email({ email, password });
      if (res.error) {
        setError("メールアドレスまたはパスワードが正しくありません。");
        return;
      }
      // 二要素が必要な場合
      if ((res.data as { twoFactorRedirect?: boolean } | null)?.twoFactorRedirect) {
        setNeedTotp(true);
        return;
      }
      await finish();
    } catch {
      setError("ログインに失敗しました。時間をおいて再度お試しください。");
    } finally {
      setLoading(false);
    }
  }

  async function onVerifyTotp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await authClient.twoFactor.verifyTotp({ code: totp });
      if (res.error) {
        setError("認証コードが正しくありません。");
        return;
      }
      await finish();
    } catch {
      setError("認証に失敗しました。");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-md">
      <PageTitle title="ログイン" subtitle="学校・企業・管理者の方はこちらから。" />
      <Card>
        {!needTotp ? (
          <form className="space-y-4" onSubmit={onSubmit}>
            <Field label="メールアドレス">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Field>
            <Field label="パスワード">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Field>
            {error && <p className="text-sm font-bold text-rose-500">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading}>
              ログイン
            </Button>
            <div className="text-center">
              <Link to="/forgot-password" className="text-xs font-bold text-brand hover:underline">
                パスワードをお忘れですか？
              </Link>
            </div>
          </form>
        ) : (
          <form className="space-y-4" onSubmit={onVerifyTotp}>
            <Field label="認証コード（TOTP）" hint="認証アプリに表示された6桁を入力">
              <Input
                inputMode="numeric"
                value={totp}
                onChange={(e) => setTotp(e.target.value)}
                required
              />
            </Field>
            {error && <p className="text-sm font-bold text-rose-500">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading}>
              認証する
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
